import { useDisclosure } from '@mantine/hooks';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { queryClient } from '@apple/retail/router';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { BulkUploadDialog } from '@apple/utils/files/components/BulkUploadDialog';
import type { Guid } from '@apple/utils/primitive';

import { plantQueryOptions } from '@apple/features/plants/queries/queries';
import {
	completeEventItemsUpload,
	getErrorReportFileName,
	getErrorReportUrl,
	uploadEventItemsTemplate,
} from '@apple/features/store-events/api/item-upload';
import { ShipmentReportLink } from '@apple/features/store-events/components/shipment-report-link';
import { useStoreEventColumns } from '@apple/features/store-events/hooks/use-store-event-columns';
import type {
	StoreEventFilters,
	StoreEventListingModel,
} from '@apple/features/store-events/models/listing';
import {
	filterableStoreEventStatusSchema,
	storeEventTypeSchema,
} from '@apple/features/store-events/models/shared';
import { storeEventQueryKeys } from '@apple/features/store-events/queries';
import { storeEventsOdataQueryOptions } from '@apple/features/store-events/queries/odata';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

const defaultFilters: StoreEventFilters = {};

export const Route = createFileRoute('/_authed/store-events/')({
	component: StoreEventsManagementRoute,
});

export function StoreEventsManagementRoute() {
	const { t } = useTranslation('store-event');
	const { auth } = Route.useRouteContext();
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const [storeEventId, setStoreEventId] = useState<Guid>('');
	const [itemUploadOpened, itemUploadActions] = useDisclosure(false);

	const tableState = useTableState<StoreEventListingModel, StoreEventFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: [{ id: 'fullKitDeliveryDate', desc: false }],
		fieldMap: [],
	});

	const [storeEventSearchQuery, plantsQuery] = useQueries({
		queries: [
			storeEventsOdataQueryOptions({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
			}),
			plantQueryOptions.plants,
		],
	});

	const handleUploadItems = (id: string) => {
		setStoreEventId(id);
		itemUploadActions.open();
	};

	const { columns } = useStoreEventColumns({
		plants: plantsQuery.data ?? [],
		onNavigateToLockedItems: storeEventId => {
			navigate({
				to: '/store-events/$storeEventId/products',
				params: { storeEventId },
				search: { hasOnlyLockedItems: true },
			});
		},
		renderShipmentReportLink: ShipmentReportLink,
		onUploadItems: handleUploadItems,
		uploadingItemsDisabled: itemUploadOpened,
	});

	const table = useTable({
		features,
		data: storeEventSearchQuery.data.rows,
		rowCount: storeEventSearchQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'warehouseCode':
						return new Map(plantsQuery.data?.map(x => [x.warehouseCode, 1]));
					case 'type':
						return new Map(storeEventTypeSchema.options.map(x => [x, 1]));
					case 'status':
						return new Map(filterableStoreEventStatusSchema.options.map(x => [x, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns,
	});

	return (
		<TableLayout
			title={t('storeEvents.title')}
			table={table}
			toolbarButtons={
				auth.hasPermission('AppleRetail.Security.Features.StoreEventManagement.Write')
					? [
							<ToolbarButton
								key='add'
								tooltip={t('common:buttons.create')}
								icon={AddIcon}
								onClick={() => {
									void navigate({ to: '/store-events/new' });
								}}
							/>,
						]
					: undefined
			}
		>
			<DataTable table={table} loading={storeEventSearchQuery.isFetching} />
			<BulkUploadDialog
				opened={itemUploadOpened}
				close={itemUploadActions.close}
				onUpload={async (file, event) =>
					await uploadEventItemsTemplate(storeEventId, file, event)
				}
				onSave={async fileId => {
					await completeEventItemsUpload(storeEventId, fileId);
					await queryClient.invalidateQueries({
						queryKey: storeEventQueryKeys.all,
					});
				}}
				errorDownloadProps={{
					getErrorReportName: fileId => getErrorReportFileName(storeEventId, fileId),
					getErrorReportUrl: () => getErrorReportUrl(storeEventId),
					fallbackFilename: `event-items-errors-${storeEventId}.xlsx`,
				}}
			/>
		</TableLayout>
	);
}
